import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addKnownOutfits,
  addOutfitsPage,
  selectHasMoreOutfits,
  selectOutfits,
} from "../app/slices/MetadataSlice";
import { selectCurrentOrganization } from "../app/slices/UserSlice";
import { RootState } from "../app/Store";
import PiLoadMoreSentinel from "../components/PiLoadMoreSentinel";
import { Outfit } from "../models/Outfit";
import { OutfitService } from "../services/OutfitService";

// pickers share one catalog, so only one page request may be in flight at a time
let pageInFlight: number | undefined;

export interface OutfitPage {
  outfits: Outfit[];
  hasMore: boolean;
  /** How many outfits exist in all, from the first page's headers; undefined until then. */
  totalCount?: number;
  isLoading: boolean;
  /** Fetches the next page; a no-op while one is loading or when all are loaded. */
  loadNextPage: () => void;
  /** A sentinel to render at the end of the list; null once everything is loaded. */
  loadMore: JSX.Element | null;
}

/**
 * The outfit catalog for pickers, loaded a page at a time. Render `loadMore`
 * (a sentinel) at the end of the picker's list; it fetches the next page when
 * it scrolls into view, so nothing loads until a picker is opened.
 */
export default function useOutfitCatalog(): OutfitPage {
  const dispatch = useDispatch();
  const outfits = useSelector(selectOutfits);
  const hasMore = useSelector(selectHasMoreOutfits);
  const loadedPages = useSelector(
    (state: RootState) => state.metadata.outfitsLoadedPages
  );
  const totalCount = useSelector((state: RootState) => state.metadata.outfitsTotalCount);
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const [isLoading, setIsLoading] = useState(false);

  const loadNextPage = useCallback(async () => {
    const page = loadedPages + 1;
    if (!orgId || !hasMore || pageInFlight === page) return;

    pageInFlight = page;
    setIsLoading(true);
    try {
      const result = await OutfitService.GetOutfitsPage(orgId, page);
      if (result) dispatch(addOutfitsPage({ page, ...result }));
    } finally {
      pageInFlight = undefined;
      setIsLoading(false);
    }
  }, [orgId, hasMore, loadedPages, dispatch]);

  const loadMore = hasMore ? (
    <PiLoadMoreSentinel onVisible={loadNextPage} isLoading={isLoading} />
  ) : null;

  return { outfits, hasMore, totalCount, isLoading, loadNextPage, loadMore };
}

/**
 * Outfits whose name contains `query` (server-side, case-insensitive), paged
 * like the catalog but kept local to the picker: a search is short-lived, so
 * it does not belong in the shared catalog. Found outfits are recorded as
 * known, so validation and the pickers' selected-outfit lookups know them.
 */
export function useOutfitSearch(query: string): OutfitPage {
  const dispatch = useDispatch();
  const orgId = useSelector(selectCurrentOrganization)?.id;
  const [state, setState] = useState<{
    query: string;
    outfits: Outfit[];
    loadedPages: number;
    totalCount?: number;
  }>({ query, outfits: [], loadedPages: 0 });
  const [isLoading, setIsLoading] = useState(false);
  // the latest query, so a response for an older one is dropped
  const latestQuery = useRef(query);

  useEffect(() => {
    latestQuery.current = query;
    setState({ query, outfits: [], loadedPages: 0 });
    setIsLoading(false);
  }, [query]);

  const current = state.query === query ? state : { query, outfits: [], loadedPages: 0 };
  const hasMore =
    query !== "" &&
    (current.totalCount === undefined || current.outfits.length < current.totalCount);

  const loadNextPage = useCallback(async () => {
    if (!orgId || !hasMore || isLoading) return;

    const page = current.loadedPages + 1;
    setIsLoading(true);
    try {
      const result = await OutfitService.GetOutfitsPage(orgId, page, query);
      if (!result || latestQuery.current !== query) return;

      dispatch(addKnownOutfits(result.outfits));
      setState((previous) =>
        previous.query !== query || previous.loadedPages !== page - 1
          ? previous
          : {
              query,
              outfits: [...previous.outfits, ...result.outfits],
              loadedPages: page,
              totalCount: result.totalCount,
            }
      );
    } finally {
      if (latestQuery.current === query) setIsLoading(false);
    }
  }, [orgId, hasMore, isLoading, current.loadedPages, query, dispatch]);

  const loadMore = hasMore ? (
    <PiLoadMoreSentinel onVisible={loadNextPage} isLoading={isLoading} />
  ) : null;

  return {
    outfits: current.outfits,
    hasMore,
    totalCount: current.totalCount,
    isLoading,
    loadNextPage,
    loadMore,
  };
}
